import type { Color, Theme } from "@mui/material";
import type { TagColor } from "../api/types";

export const getColorBrightness = (colorHex: string): number => {
  let hex = colorHex;
  if (colorHex.length === 4) {
    const [s, rr, gg, bb] = colorHex;
    hex = `${s}${rr}${rr}${gg}${gg}${bb}${bb}`;
  }

  if (hex.length !== 7) {
    return NaN;
  }

  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (0.3 * r + 0.5 * g + 0.2 * b) / 255;
};

const validHexColor = (hexStr: string) => {
  return /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexStr);
};

export const hexStringToRgb = (hexStr: string): [number, number, number] => {
  let hex = hexStr;

  if (hexStr.length === 4) {
    hex = hexStr.replace(
      /^#?([a-f\d])([a-f\d])([a-f\d])$/i,
      (_, r: string, g: string, b: string) => r + r + g + g + b + b
    );
  }

  const validHEX = validHexColor(hex);
  if (!validHEX) {
    return [-1, -1, -1];
  }

  const r = validHEX[1];
  const g = validHEX[2];
  const b = validHEX[3];

  return r && g && b ? [parseInt(r, 16), parseInt(g, 16), parseInt(b, 16)] : [-1, -1, -1];
};

export const isValidHexColor = (color: string): boolean => {
  const validHex = validHexColor(color);
  return !!(validHex && validHex[1] && validHex[2] && validHex[3]);
};

export const makeTintedBackgroundFromColor = (theme: Theme, color: string): string =>
  theme.alpha(color, theme.palette.action.selectedOpacity);

/** Same tint as `makeTintedBackgroundFromColor`, but flattened into `backgroundColor` to stay opaque on sticky elements. */
export const makeOpaqueTintedBackgroundFromColor = (theme: Theme, color: string, backgroundColor: string): string =>
  `color-mix(in srgb, ${color} calc(${theme.palette.action.selectedOpacity} * 100%), ${backgroundColor})`;

export type ThemeColorFn = (t: Theme) => string;

export const makeModeAwareAccent = (theme: Theme, color: Color): string =>
  theme.palette.mode === "dark" ? color[200] : color[700];

export const makeModeAwareAccentBackground = (theme: Theme, color: Color): string =>
  makeTintedBackgroundFromColor(theme, makeModeAwareAccent(theme, color));

export const makeModeAwareAccentSurface = (theme: Theme, color: Color): { bgcolor: string; color: string } => ({
  bgcolor: makeModeAwareAccentBackground(theme, color),
  color: makeModeAwareAccent(theme, color),
});

export const tagColorToThemeColorMap: Record<TagColor, ThemeColorFn> = {
  None: (t) => t.palette.text.secondary,
  Info: (t) => t.palette.info.main,
  Warning: (t) => t.palette.warning.main,
  Error: (t) => t.palette.error.main,
  Success: (t) => t.palette.success.main,
};
